import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useTasks } from "@/v2/hooks/useTasks";
import { TaskCard } from "@/v2/components/TaskCard";
import { useRole, ROLE_LABELS } from "@/contexts/RoleContext";
import { useAuth } from "@/contexts/AuthContext";
import { CircleCheck, Inbox as InboxIcon, AlertTriangle, Filter } from "lucide-react";
import { cn } from "@/lib/utils";
import type { TaskPriority } from "@/v2/lib/tasks";

const PRIORITY_FILTERS: { key: TaskPriority | "all"; labelKey: string; color: string }[] = [
  { key: "all", labelKey: "v2.inbox.filters.all", color: "text-text-muted" },
  { key: "urgent", labelKey: "v2.inbox.filters.urgent", color: "text-danger" },
  { key: "high", labelKey: "v2.inbox.filters.high", color: "text-warning" },
  { key: "medium", labelKey: "v2.inbox.filters.medium", color: "text-accent" },
];

export function InboxPage() {
  const { t } = useTranslation();
  const { role } = useRole();
  const { profile } = useAuth();
  const { tasks, groups, isLoading, totalBiens } = useTasks();
  const [priorityFilter, setPriorityFilter] = useState<TaskPriority | "all">("all");

  const visibleGroups = groups
    .map((g) => ({
      ...g,
      tasks: g.tasks.filter(
        (task) => priorityFilter === "all" || task.priority === priorityFilter,
      ),
    }))
    .filter((g) => g.tasks.length > 0);

  const urgent = tasks.filter((task) => task.priority === "urgent").length;
  const high = tasks.filter((task) => task.priority === "high").length;
  const medium = tasks.filter((task) => task.priority === "medium").length;

  const firstName = profile?.first_name || "";

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-text-primary flex items-center gap-2">
            <InboxIcon size={22} className="text-accent" />
            {t("v2.inbox.title", { name: firstName || ROLE_LABELS[role] })}
          </h1>
          <p className="text-sm text-text-muted mt-1">
            {t("v2.inbox.subtitle", { count: totalBiens })}
          </p>
        </div>
        <div className="flex items-center gap-2 text-sm">
          {urgent > 0 && (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-danger/10 text-danger font-medium">
              <AlertTriangle size={14} /> {t("v2.inbox.summary.urgent", { count: urgent })}
            </span>
          )}
          {high > 0 && (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-warning/10 text-warning font-medium">
              {t("v2.inbox.summary.high", { count: high })}
            </span>
          )}
          {medium > 0 && (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-accent/10 text-accent font-medium">
              {t("v2.inbox.summary.medium", { count: medium })}
            </span>
          )}
        </div>
      </div>

      {/* Filtres priorité */}
      <div className="flex items-center gap-2 mb-5 flex-wrap">
        <Filter size={14} className="text-text-muted" />
        {PRIORITY_FILTERS.map((f) => {
          const count =
            f.key === "all" ? tasks.length : tasks.filter((task) => task.priority === f.key).length;
          const isActive = priorityFilter === f.key;
          return (
            <button
              key={f.key}
              type="button"
              onClick={() => setPriorityFilter(f.key)}
              className={cn(
                "px-2.5 py-1 rounded-md text-xs font-medium cursor-pointer transition-colors border",
                isActive
                  ? "bg-accent-light text-accent border-accent"
                  : "bg-surface text-text-secondary border-border hover:border-accent/30",
              )}
            >
              <span>{t(f.labelKey)}</span>{" "}
              <span className="text-text-muted ml-0.5">({count})</span>
            </button>
          );
        })}
      </div>

      {/* Liste */}
      {isLoading ? (
        <div className="bg-surface rounded-md border border-border p-12 text-center text-text-muted">
          {t("v2.inbox.loading")}
        </div>
      ) : visibleGroups.length === 0 ? (
        <div className="bg-surface rounded-md border border-border p-12 text-center">
          <CircleCheck size={36} className="text-accent mx-auto mb-3" />
          <h3 className="text-lg font-medium text-text-primary mb-1">
            {t("v2.inbox.empty.title")}
          </h3>
          <p className="text-sm text-text-muted">
            {t("v2.inbox.empty.subtitle")}
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-6">
          {visibleGroups.map((group) => (
            <section key={group.key}>
              <h2 className="text-xs uppercase tracking-wider text-text-muted font-semibold mb-2 flex items-center gap-2">
                <span>{t(group.labelKey)}</span>
                <span className="text-text-muted/60">·</span>
                <span className="text-text-muted/60">{group.tasks.length}</span>
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {group.tasks.map((task) => (
                  <TaskCard key={task.id} task={task} />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
